// Live workspace list + file counts per workspace for a contact (Firebase uid).
// Bounded listeners: caps parallel queries and file docs per query to limit Firestore reads.
import { collection, query, where, onSnapshot, limit } from "firebase/firestore";
import { db } from "./firebase";
import { PLATFORM_SOURCES } from "./platformActivity";
import { formatWorkspaceAddress } from "./workspaceAddress";
import { extractSqftFromDoc, workspaceIdFromDoc, scanLabelFromDoc } from "./scanSqft";

const MAX_WORKSPACE_FIELDS = 6;
const MAX_MEMBER_FIELDS = 6;
const MAX_FILE_USER_FIELDS = 4;
/** Scanbrix `scans` docs use `created_by` (not `user_id`) for the uploader. */
const SCAN_USER_FIELDS = ["created_by", "createdBy", "user_id", "userId", "uploadedBy", "uid"];
/** Exported for UI copy — files queried per user-link field (multiple fields may run in parallel). */
export const WORKSPACE_SUMMARY_MAX_FILES_PER_FIELD = 120;
const MAX_FILES_PER_FIELD = WORKSPACE_SUMMARY_MAX_FILES_PER_FIELD;

function workspaceTitle(d) {
  if (!d || typeof d !== "object") return "Workspace";
  return d.name || d.title || d.workspace_name || "Workspace";
}

function fileWorkspaceId(d) {
  if (!d || typeof d !== "object") return "";
  const w =
    d.workspaceId ??
    d.workspace_id ??
    d.workspaceID ??
    (d.workspace && typeof d.workspace === "object" && d.workspace.id) ??
    (typeof d.workspace === "string" ? d.workspace : null);
  return w != null && String(w).trim() ? String(w).trim() : "";
}

function memberWorkspaceId(d) {
  if (!d || typeof d !== "object") return "";
  const w = d.workspace_id ?? d.workspaceId ?? (d.workspace && d.workspace.id) ?? d.workspace;
  return w != null && String(w).trim() ? String(w).trim() : "";
}

/**
 * @param {string} userId
 * @param {(summary: {
 *   workspaces: {
 *     id: string;
 *     title: string;
 *     address: string;
 *     fileCount: number;
 *     sqft: number|null;
 *     scans: { id: string; label: string; sqft: number }[];
 *   }[];
 *   totalFileDocs: number;
 *   filesWithoutWorkspace: number;
 *   totalSqft: number;
 * }) => void} callback
 */
export function watchContactWorkspaceUploadSummary(userId, callback, onError) {
  if (!userId) return () => {};

  const wsSource = PLATFORM_SOURCES.find((s) => s.id === "workspaces");
  const wmSource = PLATFORM_SOURCES.find((s) => s.id === "workspace_members");
  const fileSource = PLATFORM_SOURCES.find((s) => s.id === "files");
  const wsFields = (wsSource?.userFields || ["owner_user_id"]).slice(0, MAX_WORKSPACE_FIELDS);
  const wmFields = (wmSource?.userFields || ["userId", "uid", "memberId"]).slice(0, MAX_MEMBER_FIELDS);
  const fileFields = (fileSource?.userFields || ["uploadedBy"]).slice(0, MAX_FILE_USER_FIELDS);
  const scanFields = (() => {
    const fromEnv = process.env.REACT_APP_ACTIVITY_SCANS_USER_FIELDS;
    if (fromEnv && String(fromEnv).trim()) {
      return String(fromEnv)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, MAX_FILE_USER_FIELDS);
    }
    return SCAN_USER_FIELDS.slice(0, MAX_FILE_USER_FIELDS);
  })();

  const state = { wsBuckets: {}, memberBuckets: {}, fileBuckets: {}, scanBuckets: {} };

  const emit = () => {
    const wsMap = new Map();
    wsFields.forEach((field) => {
      const b = state.wsBuckets[`ws::${field}`];
      if (!b) return;
      Object.values(b).forEach((docSnap) => {
        if (!wsMap.has(docSnap.id)) {
          const data = docSnap.data();
          wsMap.set(docSnap.id, { id: docSnap.id, title: workspaceTitle(data), data });
        }
      });
    });

    wmFields.forEach((field) => {
      const b = state.memberBuckets[`wm::${field}`];
      if (!b) return;
      Object.values(b).forEach((docSnap) => {
        const wid = memberWorkspaceId(docSnap.data());
        if (!wid || wsMap.has(wid)) return;
        wsMap.set(wid, {
          id: wid,
          title: `Workspace ${wid.length > 12 ? `${wid.slice(0, 10)}…` : wid}`,
          data: {},
        });
      });
    });

    const fileDocById = new Map();
    fileFields.forEach((field) => {
      const b = state.fileBuckets[`files::${field}`];
      if (!b) return;
      Object.values(b).forEach((docSnap) => {
        fileDocById.set(docSnap.id, docSnap);
      });
    });

    const counts = new Map();
    let filesWithoutWorkspace = 0;
    fileDocById.forEach((docSnap) => {
      const wid = fileWorkspaceId(docSnap.data());
      if (!wid) {
        filesWithoutWorkspace += 1;
        return;
      }
      counts.set(wid, (counts.get(wid) || 0) + 1);
    });

    const scanDocById = new Map();
    scanFields.forEach((field) => {
      const b = state.scanBuckets[`scans::${field}`];
      if (!b) return;
      Object.values(b).forEach((docSnap) => {
        scanDocById.set(docSnap.id, docSnap);
      });
    });

    const scansByWorkspace = new Map();
    scanDocById.forEach((docSnap) => {
      const data = { id: docSnap.id, ...docSnap.data() };
      const wid = workspaceIdFromDoc(data);
      const sqft = extractSqftFromDoc(data);
      if (!wid) return;
      if (!wsMap.has(wid)) {
        wsMap.set(wid, {
          id: wid,
          title: data.workspace_name || data.workspaceName || `Workspace ${wid.slice(0, 10)}…`,
          data: {},
        });
      }
      if (sqft == null) return;
      if (!scansByWorkspace.has(wid)) scansByWorkspace.set(wid, []);
      scansByWorkspace.get(wid).push({
        id: docSnap.id,
        label: scanLabelFromDoc(data),
        sqft,
      });
    });

    const sqftByWorkspace = new Map();
    scansByWorkspace.forEach((rows, wid) => {
      const total = rows.reduce((sum, row) => sum + (row.sqft || 0), 0);
      if (total > 0) sqftByWorkspace.set(wid, total);
    });

    fileDocById.forEach((docSnap) => {
      const wid = fileWorkspaceId(docSnap.data());
      const sqft = extractSqftFromDoc(docSnap.data());
      if (!wid || sqft == null) return;
      sqftByWorkspace.set(wid, Math.max(sqftByWorkspace.get(wid) || 0, sqft));
    });

    wsMap.forEach((w) => {
      if (sqftByWorkspace.has(w.id)) return;
      const sqft = extractSqftFromDoc(w.data);
      if (sqft != null) sqftByWorkspace.set(w.id, sqft);
    });

    const workspaces = [...wsMap.values()].map((w) => {
      const scans = (scansByWorkspace.get(w.id) || []).sort((a, b) => b.sqft - a.sqft || a.label.localeCompare(b.label));
      return {
        id: w.id,
        title: w.title,
        address: formatWorkspaceAddress(w.data),
        fileCount: counts.get(w.id) || 0,
        sqft: sqftByWorkspace.get(w.id) ?? null,
        scans,
      };
    });
    workspaces.sort((a, b) => b.fileCount - a.fileCount || a.title.localeCompare(b.title));

    const totalSqft = workspaces.reduce((sum, w) => sum + (w.sqft || 0), 0);

    callback({
      workspaces,
      totalFileDocs: fileDocById.size,
      filesWithoutWorkspace,
      totalSqft,
    });
  };

  const unsubs = [];

  wsFields.forEach((field) => {
    const key = `ws::${field}`;
    const q = query(collection(db, "workspaces"), where(field, "==", userId));
    unsubs.push(
      onSnapshot(
        q,
        (snap) => {
          const b = {};
          snap.docs.forEach((d) => {
            b[d.id] = d;
          });
          state.wsBuckets[key] = b;
          emit();
        },
        (err) => {
          console.warn("[workspace summary] workspaces", field, err?.code, err?.message);
          onError?.(err);
        }
      )
    );
  });

  wmFields.forEach((field) => {
    const key = `wm::${field}`;
    const q = query(collection(db, "workspace_members"), where(field, "==", userId));
    unsubs.push(
      onSnapshot(
        q,
        (snap) => {
          const b = {};
          snap.docs.forEach((d) => {
            b[d.id] = d;
          });
          state.memberBuckets[key] = b;
          emit();
        },
        (err) => {
          console.warn("[workspace summary] workspace_members", field, err?.code, err?.message);
          onError?.(err);
        }
      )
    );
  });

  fileFields.forEach((field) => {
    const key = `files::${field}`;
    const q = query(collection(db, "files"), where(field, "==", userId), limit(MAX_FILES_PER_FIELD));
    unsubs.push(
      onSnapshot(
        q,
        (snap) => {
          const b = {};
          snap.docs.forEach((d) => {
            b[d.id] = d;
          });
          state.fileBuckets[key] = b;
          emit();
        },
        (err) => {
          console.warn("[workspace summary] files", field, err?.code, err?.message);
          onError?.(err);
        }
      )
    );
  });

  scanFields.forEach((field) => {
    const key = `scans::${field}`;
    const q = query(collection(db, "scans"), where(field, "==", userId), limit(MAX_FILES_PER_FIELD));
    unsubs.push(
      onSnapshot(
        q,
        (snap) => {
          const b = {};
          snap.docs.forEach((d) => {
            b[d.id] = d;
          });
          state.scanBuckets[key] = b;
          emit();
        },
        (err) => {
          console.warn("[workspace summary] scans", field, err?.code, err?.message);
          onError?.(err);
        }
      )
    );
  });

  return () => unsubs.forEach((u) => u());
}

const CONTACT_FILE_USER_FIELDS = ["user_id", "userId", "created_by", "uploadedBy"];

/** Live `files` docs for a contact (workspace photos and scan uploads). */
export function watchContactFiles(userId, callback, onError) {
  if (!userId) return () => {};
  const state = {};
  const emit = () => {
    const byId = new Map();
    CONTACT_FILE_USER_FIELDS.forEach((field) => {
      const b = state[field];
      if (!b) return;
      Object.values(b).forEach((docSnap) => {
        byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
      });
    });
    callback(Array.from(byId.values()));
  };
  const unsubs = CONTACT_FILE_USER_FIELDS.map((field) => {
    const q = query(collection(db, "files"), where(field, "==", userId), limit(MAX_FILES_PER_FIELD));
    return onSnapshot(
      q,
      (snap) => {
        const b = {};
        snap.docs.forEach((d) => {
          b[d.id] = d;
        });
        state[field] = b;
        emit();
      },
      (err) => {
        console.warn("[contact files]", field, err?.code, err?.message);
        onError?.(err);
      }
    );
  });
  return () => unsubs.forEach((u) => u());
}
